import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { Loader2, AlertCircle, CreditCard, ArrowRight, Clock } from 'lucide-react';

export function PaymentAlertsWidget() {
  const { t, i18n } = useTranslation('dashboard');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const { data, isLoading, error } = trpc.dashboard.upcomingPaymentDues.useQuery({ days: 7 });

  if (isLoading) {
    return (
      <div className="luxury-card h-[320px] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="luxury-card h-[320px] flex items-center justify-center">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('chartError')}</p>
      </div>
    );
  }

  const totalDue = data?.reduce((sum, item) => sum + (item.amount - item.paidAmount), 0) || 0;

  return (
    <div className="luxury-card p-0 overflow-hidden">
      <div className="flex items-center justify-between border-b border-[hsl(var(--border))] px-6 py-4">
        <div>
          <h2 className="font-display text-lg font-semibold flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-orange-500" />
            {t('paymentsDue')}
          </h2>
          <p className="text-sm text-[hsl(var(--muted-foreground))]">{t('next7Days')}</p>
        </div>
        {data && data.length > 0 && (
          <div className="text-right">
            <p className="font-display text-xl font-semibold text-orange-600">
              {formatCurrency(totalDue)}
            </p>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">{t('totalDue')}</p>
          </div>
        )}
      </div>

      {!data || data.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <div className="rounded-2xl bg-emerald-50 p-5">
            <CreditCard className="h-10 w-10 text-emerald-600" />
          </div>
          <p className="mt-4 font-display font-medium text-emerald-700">{t('noPaymentsDue')}</p>
          <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] text-center">
            {t('allPaymentsOnTrack')}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-[hsl(var(--border))] max-h-[220px] overflow-y-auto">
          {data.slice(0, 5).map((item) => {
            const dueDate = new Date(item.dueDate);
            const today = new Date();
            const daysUntilDue = Math.ceil(
              (dueDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)
            );
            const isOverdue = item.status === 'OVERDUE';
            const isUrgent = daysUntilDue <= 2;

            return (
              <Link
                key={item.id}
                to={`/bookings/${item.bookingId}`}
                className="flex items-center justify-between px-6 py-3 hover:bg-[hsl(var(--muted))/30] transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-medium truncate">
                      {item.bookingReference}
                    </span>
                    {(isOverdue || isUrgent) && (
                      <span
                        className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                          isOverdue
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}
                      >
                        <AlertCircle className="h-3 w-3" />
                        {isOverdue ? t('overdue') : t('urgent')}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] truncate">
                    {item.customer?.name || t('unknownCustomer')} • {item.packageName}
                  </p>
                </div>
                <div className="text-right ml-4">
                  <p className="font-display font-semibold">
                    {formatCurrency(item.amount - item.paidAmount)}
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] flex items-center justify-end gap-1">
                    <Clock className="h-3 w-3" />
                    {dueDate.toLocaleDateString(dateLocale, { month: 'short', day: 'numeric' })}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {data && data.length > 5 && (
        <div className="border-t border-[hsl(var(--border))] px-6 py-3">
          <Link
            to="/bookings?filter=payment-due"
            className="flex items-center justify-center gap-2 text-sm font-medium text-[hsl(var(--gold))] hover:underline"
          >
            {t('viewAllPayments')} ({data.length})
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      )}
    </div>
  );
}
